/* RESTORING CanaisConfig.js - PART 1 */
import { CircularProgress } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import v1_actionWboTemplates from 'src/actions/v1/meta/wbo/v1_actionWboTemplates';
import { transformCategoryTemplateWhatsApp } from 'src/utils/meta.utils';
import ChannelDialogflow from './TypesChannels/ChannelDialogflow';
import { CANAL_DETALHE_DESIGN as D } from './CanaisDetails.design';
import ChannelDirect from './TypesChannels/ChannelDirect';
import ChannelHealth from './TypesChannels/ChannelHealth';
// ChannelWebchat / ChannelWebchatHours movidos para a Central do Widget (widget-cms/ProjetoDetail).
import ChannelLaisAiV2 from './TypesChannels/ChannelLaisAiV2';
import { listChatbots } from 'src/actions/v1/chatbots/v1_actionChatbots';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Textarea } from 'src/@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import { MultiSelect } from 'src/@/components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { verifyFeatures, verifyRules } from 'src/utils/functions';
import TemplatesWhatsappPage from '../templates-whatsapp';
import TemplatesMessengerPage from '../templates-messenger';
import { SectionHeader, ToggleRow, ChoiceCard, MessageEditor } from './CanaisFormParts';

// Seletor de template WABA reutilizado em feedback e transferência.
function WhatsAppTemplatePicker({
    t,
    D,
    enabled,
    obrigatorio,
    template,
    templateVariaveis,
    selectedTemplateId,
    templates,
    loadingTemplates,
    normalizarComponents,
    onSelectTemplate,
    onFetchTemplates,
    onChangeVariavel,
    alertBody,
    messageRequiredHint,
    showMessageRequiredHint,
}) {
    if (!enabled) return null;

    return (
        <div className={D.section}>
            {alertBody && obrigatorio && !template && (
                <p className={`${D.hintError} mb-3`}>{alertBody}</p>
            )}
            <div className={D.cardHead}>
                <div className="min-w-0">
                    <h4 className={D.sectionTitle}>
                        {t('channels.messages.templateFieldLabel', 'Template do WhatsApp')}{' '}
                        {obrigatorio && <span className={D.required}>*</span>}
                    </h4>
                    <p className={D.sectionSubtitle}>
                        {t(
                            'channels.messages.templateWindowHelp',
                            'O template é usado fora da janela de 24 horas; o texto acima, dentro dela.'
                        )}
                    </p>
                </div>
                <span className={`${D.badge} ${template ? D.badgeOk : D.badgeWarn}`}>
                    {template
                        ? t('channels.messages.templateSelectedBadge', 'Template definido')
                        : t('channels.messages.templatePendingBadge', 'Template obrigatório')}
                </span>
            </div>

            <div className="mt-4 max-w-[520px]">
                <Select
                    value={selectedTemplateId}
                    onValueChange={onSelectTemplate}
                    onOpenChange={(open) => {
                        if (open && !loadingTemplates) onFetchTemplates();
                    }}
                    disabled={loadingTemplates}
                >
                    <SelectTrigger className={D.select}>
                        {loadingTemplates ? (
                            <div className="flex items-center gap-2">
                                <CircularProgress size={16} />
                                <span className="text-[#a1a1aa]">
                                    {t('channels.messages.loadingTemplatesLabel', 'Carregando templates...')}
                                </span>
                            </div>
                        ) : (
                            <SelectValue
                                placeholder={t('channels.messages.templateSelectPlaceholder', 'Selecione um template')}
                            >
                                {template ? template.name : null}
                            </SelectValue>
                        )}
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px]">
                        <SelectItem value="none">
                            <span className="text-[#a1a1aa]">
                                {t('channels.messages.templateNoneOption', 'Nenhum template selecionado')}
                            </span>
                        </SelectItem>
                        {templates.length === 0 ? (
                            <div className={D.emptyState}>
                                <Iconify icon="mdi:file-document-remove-outline" width={32} className="mx-auto mb-2 text-[#d4d4d8]" />
                                <p>{t('channels.messages.templateEmptyState', 'Nenhum template disponível')}</p>
                            </div>
                        ) : (
                            templates.map((tpl) => {
                                const comps = normalizarComponents(tpl?.components || []);
                                const conteudo = comps?.find((c) => c.type === 'BODY')?.text || '';
                                const temVariaveis = (conteudo.match(/\{\{\d+\}\}/g) || []).length > 0;
                                const templateName = tpl.name || tpl.templateName || t('channels.messages.templateNoNameFallback', 'Sem nome');
                                const categoria = transformCategoryTemplateWhatsApp(tpl?.category) || 'N/A';
                                const templateIdValue = String(tpl.templateId || tpl._id);
                                return (
                                    <SelectItem key={templateIdValue} value={templateIdValue}>
                                        <div className="flex flex-col gap-1 py-1 min-w-0">
                                            <span className="font-medium text-sm text-[#18181b]">{templateName}</span>
                                            <span className="flex items-center gap-2 flex-wrap">
                                                <span className="text-xs px-2 py-0.5 bg-[#f4f4f5] text-[#52525b] rounded">{categoria}</span>
                                                {temVariaveis && (
                                                    <span className="text-xs px-2 py-0.5 bg-[#ede9fe] text-[#5b21b6] rounded">
                                                        {t('channels.messages.templateWithVariablesBadge', 'Com variáveis')}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-xs text-[#a1a1aa] truncate max-w-[360px]">
                                                {conteudo || t('channels.messages.templateNoContentFallback', 'Sem conteúdo')}
                                            </span>
                                        </div>
                                    </SelectItem>
                                );
                            })
                        )}
                    </SelectContent>
                </Select>
                {!template && obrigatorio && (
                    <p className={D.hintError}>
                        {t('channels.messages.templateRequiredHint', 'Selecione um template (obrigatório para WhatsApp Business)')}
                    </p>
                )}
            </div>

            {showMessageRequiredHint && (
                <p className={`${D.hintWarn} mt-3`}>{messageRequiredHint}</p>
            )}

            {template && (
                <div className={`${D.editorGridNarrow} mt-5`}>
                    <div className="min-w-0">
                        {templateVariaveis && Object.keys(templateVariaveis).length > 0 ? (
                            <div className="flex flex-col gap-3">
                                <span className={D.label}>{t('channels.messages.templateVariablesTitle', 'Variáveis do Template')}</span>
                                {Object.keys(templateVariaveis)
                                    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
                                    .map((variavelKey) => (
                                        <div key={variavelKey}>
                                            <label className={`${D.label} flex items-center gap-2`} htmlFor={`tpl-var-${variavelKey}`}>
                                                <span className={D.stepNumber}>{variavelKey}</span>
                                                {t('channels.messages.templateVariableLabel', 'Variável {{key}}', { key: variavelKey })}
                                            </label>
                                            <Input
                                                id={`tpl-var-${variavelKey}`}
                                                className={D.input}
                                                value={templateVariaveis[variavelKey] || ''}
                                                onChange={(e) => onChangeVariavel(variavelKey, e.target.value)}
                                                placeholder={t('channels.messages.templateVariablePlaceholder', 'Digite o valor para a variável {{varToken}}', {
                                                    varToken: `{{${variavelKey}}}`,
                                                })}
                                            />
                                            {!templateVariaveis[variavelKey] && (
                                                <p className={D.hintWarn}>
                                                    {t('channels.messages.templateVariableRequiredHint', 'Esta variável precisa ser preenchida')}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <p className={D.noticeInfo}>
                                {t('channels.messages.templateNoVariables', 'Este template não tem variáveis para preencher.')}
                            </p>
                        )}
                    </div>
                    <div className="min-w-0">
                        <div className={D.previewLabel}>{t('channels.messages.templatePreviewLabel', 'Prévia do template')}</div>
                        <div className={D.previewShell}>
                            <div className={D.previewBubble}>
                                {template.conteudo?.split(/(\{\{\d+\}\})/g).map((parte, index) => {
                                    const match = parte.match(/\{\{(\d+)\}\}/);
                                    if (!match) return <span key={index}>{parte}</span>;
                                    const valor = templateVariaveis?.[match[1]] || '';
                                    return (
                                        <span
                                            key={index}
                                            className={
                                                valor
                                                    ? 'px-1.5 py-0.5 rounded bg-[#ede9fe] text-[#5b21b6] font-medium'
                                                    : 'px-1.5 py-0.5 rounded border border-dashed border-[#ddd6fe] text-[#8b5cf6] italic'
                                            }
                                        >
                                            {valor || parte}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ----------------------------------------------------------------------


function AiSchedulePauseSection({ config, onChange, listUsers, listSectors }) {
    const { t } = useTranslation(['settings', 'common']);
    const DIAS_SEMANA = [
        { value: 0, label: t('dates.weekdays.sunday', 'Domingo', { ns: 'common' }) },
        { value: 1, label: t('dates.weekdays.monday', 'Segunda-feira', { ns: 'common' }) },
        { value: 2, label: t('dates.weekdays.tuesday', 'Terça-feira', { ns: 'common' }) },
        { value: 3, label: t('dates.weekdays.wednesday', 'Quarta-feira', { ns: 'common' }) },
        { value: 4, label: t('dates.weekdays.thursday', 'Quinta-feira', { ns: 'common' }) },
        { value: 5, label: t('dates.weekdays.friday', 'Sexta-feira', { ns: 'common' }) },
        { value: 6, label: t('dates.weekdays.saturday', 'Sábado', { ns: 'common' }) },
    ];
    const pause = config?.aiSchedulePause || {};
    const status = !!pause.status;
    const action = pause.action || 'nothing';
    const schedules = Array.isArray(pause.schedules) ? pause.schedules : [];
    const direcionaUsuarios = Array.isArray(pause.direciona_usuarios) ? pause.direciona_usuarios : [];
    const direcionaSetores = Array.isArray(pause.direciona_setores) ? pause.direciona_setores : [];

    const isPausingNow = useMemo(() => {
        if (!status || schedules.length === 0) return false;
        try {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone: 'America/Sao_Paulo',
                weekday: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }).formatToParts(new Date());
            const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
            const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
            const dayNow = weekdayMap[map.weekday];
            const minutesNow = parseInt(map.hour, 10) * 60 + parseInt(map.minute, 10);
            return schedules.some((s) => {
                if (Number(s.day) !== dayNow) return false;
                const [hs, ms] = (s.startTime || '').split(':').map(Number);
                const [he, me] = (s.endTime || '').split(':').map(Number);
                if (Number.isNaN(hs) || Number.isNaN(he)) return false;
                const start = hs * 60 + ms;
                const end = he * 60 + me;
                return minutesNow >= start && minutesNow < end;
            });
        } catch {
            return false;
        }
    }, [status, schedules]);

    const diasConfigurados = useMemo(() => new Set(schedules.map((s) => Number(s.day))).size, [schedules]);

    const usuariosOptions = useMemo(
        () =>
            (listUsers || []).map((u) => ({
                value: u._id,
                label: u.nomeFantasia || u.nome || u.email || t('labels.user', 'Usuário', { ns: 'common' }),
            })),
        [listUsers]
    );
    const setoresOptions = useMemo(
        () => (listSectors || []).map((s) => ({ value: s._id, label: s.nome_setor || s.nome || t('channels.schedulePause.sectorFallbackLabel', 'Setor') })),
        [listSectors]
    );

    const setField = (name, value) => {
        onChange({ target: { name, value } });
    };

    const updateSchedule = (idx, patch) => {
        const next = schedules.map((s, i) => (i === idx ? { ...s, ...patch } : s));
        setField('aiSchedulePause.schedules', next);
    };

    const moveFaixasToDay = (fromDay, toDay) => {
        if (fromDay === toDay) return;
        const next = schedules.map((s) => (s.day === fromDay ? { ...s, day: toDay } : s));
        setField('aiSchedulePause.schedules', next);
    };

    const addScheduleForDay = (day) => {
        setField('aiSchedulePause.schedules', [
            ...schedules,
            { day, startTime: '08:00', endTime: '18:00' },
        ]);
    };

    const addSchedule = () => {
        const used = new Set(schedules.map((s) => s.day));
        let day = 1;
        for (let d = 1; d <= 6; d++) {
            if (!used.has(d)) {
                day = d;
                break;
            }
        }
        if (used.size === 7) day = 1;
        addScheduleForDay(day);
    };

    const removeSchedule = (idx) => {
        setField('aiSchedulePause.schedules', schedules.filter((_, i) => i !== idx));
    };

    const groupedByDay = useMemo(() => {
        const map = new Map();
        schedules.forEach((s, idx) => {
            const day = s.day ?? 1;
            if (!map.has(day)) map.set(day, []);
            map.get(day).push({ ...s, idx });
        });
        return [...map.entries()].sort((a, b) => a[0] - b[0]);
    }, [schedules]);

    const dayBadgeColors = [
        'bg-rose-50 text-rose-700 border-rose-200',
        'bg-blue-50 text-blue-700 border-blue-200',
        'bg-amber-50 text-amber-700 border-amber-200',
        'bg-emerald-50 text-emerald-700 border-emerald-200',
        'bg-violet-50 text-violet-700 border-violet-200',
        'bg-indigo-50 text-indigo-700 border-indigo-200',
        'bg-pink-50 text-pink-700 border-pink-200',
    ];

    const actionLabel = action === 'redirect' ? 'Direcionar' : 'Pausar IA';
    const targetsCount = direcionaUsuarios.length + direcionaSetores.length;

    return (
        <div
            className={`mt-6 bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${
                status
                    ? 'border-violet-200 shadow-[0_4px_24px_-12px_rgba(124,58,237,0.25)]'
                    : 'border-gray-100'
            }`}
        >
            <div
                className={`relative ${
                    status ? 'bg-gradient-to-br from-violet-50/80 via-white to-white' : 'bg-white'
                }`}
            >
                {status && (
                    <div
                        aria-hidden
                        className="absolute inset-0 opacity-[0.04] pointer-events-none"
                        style={{
                            backgroundImage:
                                'radial-gradient(circle at 1px 1px, #7C3AED 1px, transparent 0)',
                            backgroundSize: '16px 16px',
                        }}
                    />
                )}

                <div className="relative flex items-start justify-between gap-4 p-5">
                    <div className="flex items-start gap-4 min-w-0 flex-1">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="text-[15px] font-semibold text-gray-900 leading-tight">
                                    {t('channels.schedulePause.title', 'Silenciar IA por horário')}
                                </h4>
                                {status && (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold uppercase tracking-wider">
                                        <span className="relative flex w-1.5 h-1.5">
                                            <span className="absolute inset-0 rounded-full bg-violet-500 animate-ping opacity-75" />
                                            <span className="relative w-1.5 h-1.5 rounded-full bg-violet-600" />
                                        </span>
                                        {t('status.active', 'Ativo', { ns: 'common' })}
                                    </span>
                                )}
                                {isPausingNow && (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider">
                                        <Iconify icon="mdi:pause-circle" width={11} />
                                        {t('channels.schedulePause.pausingNowBadge', 'Pausando agora')}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                {t('channels.schedulePause.description', 'Define dias e faixas em que a IA não deve responder automaticamente.')}
                            </p>

                        </div>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <Switch
                            checked={status}
                            onCheckedChange={(value) => setField('aiSchedulePause.status', value)}
                            className="data-[state=checked]:bg-[#7C3AED] scale-110"
                        />
                        <span
                            className={`text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                                status ? 'text-[#7C3AED]' : 'text-gray-400'
                            }`}
                        >
                            {status ? 'On' : 'Off'}
                        </span>
                    </div>
                </div>
            </div>

            {status && (
                <div className="px-5 pb-5">
                    <Separator className="mb-5 bg-gray-200/60" />

                    <div className="flex flex-col gap-5">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 block">
                                {t('channels.schedulePause.whenLabel', 'Quando silenciada')}
                            </Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {[
                                    {
                                        value: 'nothing',
                                        icon: 'mdi:bell-off-outline',
                                        title: t('channels.schedulePause.actionNothingTitle', 'Não fazer nada'),
                                        desc: t('channels.schedulePause.actionNothingDesc', 'Apenas não disparar a IA, deixar o chat seguir o curso natural.'),
                                    },
                                    {
                                        value: 'redirect',
                                        icon: 'mdi:account-arrow-right-outline',
                                        title: t('channels.schedulePause.actionRedirectTitle', 'Direcionar para setor/usuário'),
                                        desc: t('channels.schedulePause.actionRedirectDesc', 'Cria o chat como "Pendente" com o direcionamento configurado.'),
                                    },
                                ].map((opt) => {
                                    const selected = action === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setField('aiSchedulePause.action', opt.value)}
                                            className={`text-left p-4 rounded-xl border transition-all duration-200 ${
                                                selected
                                                    ? 'border-[#7C3AED] bg-violet-50/50 shadow-sm shadow-[#7C3AED]/10'
                                                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div
                                                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                                                        selected ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-500'
                                                    }`}
                                                >
                                                    <Iconify icon={opt.icon} width={18} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-sm font-semibold text-gray-900">{opt.title}</span>
                                                        <span
                                                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                                selected ? 'border-[#7C3AED] bg-[#7C3AED]' : 'border-gray-300 bg-white'
                                                            }`}
                                                        >
                                                            {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{opt.desc}</p>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {action === 'redirect' && (
                            <div className="p-4 bg-gradient-to-br from-violet-50/60 to-white rounded-xl border border-violet-100">
                                <div className="flex items-center gap-2 mb-3">
                                    <Iconify icon="mdi:account-group-outline" width={16} className="text-violet-600" />
                                    <Label className="text-xs font-semibold uppercase tracking-wider text-violet-700">
                                        {t('channels.schedulePause.targetingLabel', 'Direcionamento')}
                                    </Label>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-[11px] font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                                            <Iconify icon="mdi:account-outline" width={12} />
                                            {t('channels.schedulePause.usersLabel', 'Usuários')}
                                        </Label>
                                        <MultiSelect
                                            key={`aisp-users-${listUsers ? listUsers.length : 0}`}
                                            options={usuariosOptions}
                                            defaultValue={direcionaUsuarios}
                                            onValueChange={(v) => setField('aiSchedulePause.direciona_usuarios', v)}
                                            placeholder={t('channels.schedulePause.usersPlaceholder', 'Selecione usuários')}
                                            maxCount={2}
                                            className="bg-white border-gray-200 rounded-xl"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-[11px] font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                                            <Iconify icon="mdi:domain" width={12} />
                                            {t('channels.schedulePause.sectorsLabel', 'Setores')}
                                        </Label>
                                        <MultiSelect
                                            key={`aisp-sectors-${listSectors ? listSectors.length : 0}`}
                                            options={setoresOptions}
                                            defaultValue={direcionaSetores}
                                            onValueChange={(v) => setField('aiSchedulePause.direciona_setores', v)}
                                            placeholder={t('channels.schedulePause.sectorsPlaceholder', 'Selecione setores')}
                                            maxCount={2}
                                            className="bg-white border-gray-200 rounded-xl"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        {t('channels.schedulePause.intervalsLabel', 'Intervalos de silêncio')}
                                    </Label>
                                    {schedules.length > 0 && (
                                        <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">
                                            {schedules.length}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={addSchedule}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-xs font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95"
                                >
                                    <Iconify icon="eva:plus-outline" width={14} />
                                    {t('channels.schedulePause.addIntervalButton', 'Adicionar intervalo')}
                                </button>
                            </div>

                            {schedules.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50/60 border-2 border-dashed border-gray-200 rounded-xl">
                                    <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center mb-3">
                                        <Iconify icon="mdi:calendar-clock-outline" width={24} className="text-gray-400" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-700">{t('channels.schedulePause.emptyTitle', 'Nenhum intervalo configurado')}</p>
                                    <p className="text-xs text-gray-500 mt-1 text-center max-w-xs">
                                        {t('channels.schedulePause.emptyDescription', 'Adicione ao menos um dia e horário para que a IA pare de responder no período.')}
                                    </p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {groupedByDay.map(([day, faixas]) => {
                                        const dayBadge = dayBadgeColors[day] || dayBadgeColors[0];
                                        const dayLabel = DIAS_SEMANA[day]?.label || t('channels.schedulePause.dayFallback', 'Dia');
                                        const usedDays = new Set(schedules.map((s) => s.day));
                                        return (
                                            <div
                                                key={day}
                                                className="group flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:border-violet-200 hover:shadow-sm transition-all"
                                            >
                                                <Select
                                                    value={String(day)}
                                                    onValueChange={(value) => moveFaixasToDay(day, Number(value))}
                                                >
                                                    <SelectTrigger
                                                        className={`shrink-0 h-8 w-auto min-w-[130px] gap-1.5 border text-xs font-semibold rounded-lg px-2.5 ${dayBadge} hover:opacity-90 focus:ring-0`}
                                                    >
                                                        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                                                        <SelectValue>{dayLabel}</SelectValue>
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {DIAS_SEMANA.map((d) => (
                                                            <SelectItem
                                                                key={d.value}
                                                                value={String(d.value)}
                                                                disabled={d.value !== day && usedDays.has(d.value)}
                                                                className="text-xs"
                                                            >
                                                                {d.label}
                                                                {d.value !== day && usedDays.has(d.value) && (
                                                                    <span className="text-[10px] text-gray-400 ml-2">{t('channels.schedulePause.dayInUseHint', 'já em uso')}</span>
                                                                )}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>

                                                <div className="flex flex-wrap items-center gap-2 flex-1">
                                                    {faixas.map((f, faixaIdx) => (
                                                        <div
                                                            key={f.idx}
                                                            className="flex items-center gap-1.5 px-1.5 py-1 bg-gray-50 rounded-lg border border-gray-200"
                                                        >
                                                            <div className="flex items-center gap-1 px-1.5">
                                                                <Iconify icon="mdi:clock-outline" width={12} className="text-gray-500" />
                                                                <input
                                                                    type="time"
                                                                    value={f.startTime || ''}
                                                                    onChange={(e) => updateSchedule(f.idx, { startTime: e.target.value })}
                                                                    className="bg-transparent text-xs font-medium text-gray-800 focus:outline-none w-[64px]"
                                                                />
                                                            </div>
                                                            <Iconify icon="eva:arrow-forward-outline" width={12} className="text-gray-400" />
                                                            <div className="flex items-center gap-1 px-1.5">
                                                                <Iconify icon="mdi:clock-outline" width={12} className="text-gray-500" />
                                                                <input
                                                                    type="time"
                                                                    value={f.endTime || ''}
                                                                    onChange={(e) => updateSchedule(f.idx, { endTime: e.target.value })}
                                                                    className="bg-transparent text-xs font-medium text-gray-800 focus:outline-none w-[64px]"
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeSchedule(f.idx)}
                                                                className="ml-0.5 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                                                title={t('channels.schedulePause.removeIntervalTitle', 'Remover faixa')}
                                                            >
                                                                <Iconify icon="eva:close-outline" width={14} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => addScheduleForDay(day)}
                                                        className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-md transition-colors"
                                                        title={t('channels.schedulePause.addIntervalToDayTitle', 'Adicionar faixa neste dia')}
                                                    >
                                                        <Iconify icon="eva:plus-outline" width={12} />
                                                        {t('channels.schedulePause.addIntervalToDayButton', 'Faixa')}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="flex items-center gap-1.5 mt-3 px-2.5 py-1.5 bg-gray-50 rounded-lg w-fit">
                                <Iconify icon="mdi:earth" width={12} className="text-gray-500" />
                                <span className="text-[11px] text-gray-600 font-medium">{t('channels.schedulePause.timezoneLabel', 'Fuso horário: {{timezone}}', { timezone: 'America/Sao_Paulo' })}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function CanaisConfig({
    isEdit,
    canal,
    onChange,
    onChangeChannel,
    onChangeAutocomplete,
    listUsers,
    listSectors,
    config,
    onSubmit,
    cliente,
    user,
    changeAgent,
    // Cabeçalho da tela (voltar, identidade, salvar). Vem pronto do CanaisDetails
    // porque no design ele e a barra de abas formam um único bloco fixo.
    header,
    // Cartões de conexão da plataforma (Telegram / manual).
    // Vêm prontos do CanaisDetails e abrem a aba "Configuração".
    connectionBlocks,
    // Vínculo com conta social (Instagram, WhatsApp, etc.) — renderizado
    // dentro do cartão de Identificação.
    identityLinkBlock,
}) {
    const { t } = useTranslation(['settings', 'common']);
    const navigate = useNavigate();
    const choices = [
        {
            id: 1,
            name: t('channels.strategies.laisaiV2Name', 'Agente IA v2'),
            description: t('channels.strategies.laisaiV2Description', 'Agente simples de IA'),
            value: 'laisai_v2',
            icon: 'mdi:robot',
        },
        {
            id: 2,
            name: t('channels.strategies.directName', 'Direcionamento Direto'),
            description: t('channels.strategies.directDescription', 'Direcionamento direto para atendimento humano ou bot'),
            value: 'direct',
            icon: 'mdi:arrow-right-bold',
        },
        {
            id: 3,
            name: t('channels.strategies.dialogflowBasicName', 'DialogFlow'),
            description: t('channels.strategies.dialogflowBasicDescription', 'Integração com DialogFlow'),
            value: 'dialogflow_basic',
            icon: 'mdi:chat-processing',
        },
        {
            id: 4,
            name: t('channels.strategies.flowbuilderName', 'Customizado'),
            description: t('channels.strategies.flowbuilderDescription', 'Crie fluxos complexos de atendimento'),
            value: 'flowbuilder',
            icon: 'mdi:puzzle',
        },
        {
            id: 5,
            name: t('channels.strategies.laisaiV3Name', 'Agente IA (v3)'),
            description: t('channels.strategies.laisaiV3Description', 'Crie fluxos complexos de atendimento usando IA e ferramentas'),
            value: 'laisai_v3',
            icon: 'mdi:robot-outline',
        },
        {
            id: 6,
            name: t('channels.strategies.laisAgentsName', 'Agente IA (v3.1)'),
            description: '',
            value: 'lais_agents',
            icon: 'mdi:robot-industrial',
        },
        {
            id: 2,
            name: t('channels.strategies.apiName', 'Api Whatsapp'),
            description: t('channels.strategies.apiDescription', 'Integre seu sistema com nossa api'),
            value: 'api',
            icon: 'mdi:whatsapp',
        },
        {
            id: 8,
            name: t('channels.strategies.chatbotName', 'Chatbot'),
            description: t('channels.strategies.chatbotDescription', 'Crie chatbots para atendimento'),
            value: 'chatbot',
            icon: 'mdi:whatsapp',
        },
    ];
    const channelsList = [
        {
            id: 1,
            name: 'Whatsapp',
            description: t('channels.list.whatsappDescription', 'Direcionamento direto para atendimento humano ou bot'),
            value: 'whatsapp',
            color: 'green-500',
            icon: 'mage:whatsapp-filled',
            disabled: false,
        },
        {
            id: 2,
            name: 'Whatsapp Business Oficial',
            description: t('channels.list.whatsappBusinessDescription', 'Direcionamento direto para atendimento humano ou bot'),
            value: 'whatsapp_business',
            color: 'blue-500',
            icon: 'uil:whatsapp-alt',
            disabled: false,
        },
        {
            id: 3,
            name: 'Instagram Direct',
            description: t('channels.list.instagramDescription', 'Direct do Instagram vinculado a uma página do Facebook'),
            value: 'instagram',
            color: 'pink-500',
            icon: 'mdi:instagram',
            disabled: false,
        },
        {
            id: 4,
            name: 'Facebook Messenger',
            description: t('channels.list.messengerDescription', 'Messenger da sua página do Facebook'),
            value: 'messenger',
            color: 'blue-500',
            icon: 'ic:round-facebook',
            disabled: false,
        },
        {
            id: 5,
            name: 'Webchat',
            description: t('channels.list.webchatDescription', 'Popup de suporte para o seu site'),
            value: 'webchat',
            color: 'violet-500',
            icon: 'streamline-plump:web',
            disabled: false,
        },
        {
            id: 6,
            name: 'Telegram',
            description: t('channels.list.telegramDescription', 'Bot do Telegram conectado por token do @BotFather'),
            value: 'telegram',
            color: 'sky-500',
            icon: 'logos:telegram',
            disabled: false,
        },
    ];
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [chatbotsList, setChatbotsList] = useState([]);
    const [loadingChatbots, setLoadingChatbots] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(() =>
        config?.generalConfig?.feedbackTemplate?.templateId ? String(config.generalConfig.feedbackTemplate.templateId) : 'none'
    );
    const [selectedTransferTemplateId, setSelectedTransferTemplateId] = useState(() =>
        config?.generalConfig?.transferTemplate?.templateId ? String(config.generalConfig.transferTemplate.templateId) : 'none'
    );
    const isAdmin = user?.level === 'root';

    // A plataforma é definida na criação/conexão do canal; aqui ela só é exibida.
    const plataformaAtual = channelsList.find((item) => item.value === canal?.platform);

    const isWhatsAppBusiness = useMemo(() => {
        return canal?.platform === 'whatsapp_business';
    }, [canal?.platform]);

    const templateObrigatorio = useMemo(() => {
        return isWhatsAppBusiness && config?.generalConfig?.feedback;
    }, [isWhatsAppBusiness, config?.generalConfig?.feedback]);

    const transferTemplateObrigatorio = useMemo(() => {
        return isWhatsAppBusiness && config?.generalConfig?.msgTransfer;
    }, [isWhatsAppBusiness, config?.generalConfig?.msgTransfer]);

    const normalizarComponents = (components) => {
        if (!Array.isArray(components)) return [];
        return components.map((c) => ({
            ...c,
            type: typeof c.type === 'string' ? c.type.toUpperCase() : c.type,
        }));
    };

    const extrairVariaveisTemplate = (components) => {
        const variaveis = {};
        if (!components || !Array.isArray(components)) {
            return variaveis;
        }
        components.forEach((component) => {
            if (component.text) {
                const matches = component.text.match(/\{\{(\d+)\}\}/g);
                if (matches) {
                    matches.forEach((match) => {
                        const numero = match.replace(/\{\{|\}\}/g, '');
                        variaveis[numero] = '';
                    });
                }
            }
            if (component.buttons && Array.isArray(component.buttons)) {
                component.buttons.forEach((button) => {
                    if (button.text) {
                        const matches = button.text.match(/\{\{(\d+)\}\}/g);
                        if (matches) {
                            matches.forEach((match) => {
                                const numero = match.replace(/\{\{|\}\}/g, '');
                                variaveis[numero] = '';
                            });
                        }
                    }
                });
            }
        });
        return variaveis;
    };

    const buscarTemplates = async () => {
        const idCliente = canal?.cliente_id || user?.clienteId;
        if (!idCliente) return;
        try {
            setLoadingTemplates(true);
            const res = await v1_actionWboTemplates(idCliente);
            setTemplates(res.data?.data?.templates || []);
        } catch (error) {
            console.log('Erro ao buscar templates:', error);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleSelecionarTemplate = (templateId) => {
        selecionarTemplatePorTipo(templateId, 'feedback');
    };

    const handleSelecionarTransferTemplate = (templateId) => {
        selecionarTemplatePorTipo(templateId, 'transfer');
    };

    const selecionarTemplatePorTipo = (templateId, tipo) => {
        const isTransfer = tipo === 'transfer';
        const templateField = isTransfer ? 'generalConfig.transferTemplate' : 'generalConfig.feedbackTemplate';
        const variaveisField = isTransfer ? 'generalConfig.transferTemplateVariaveis' : 'generalConfig.feedbackTemplateVariaveis';
        const setSelected = isTransfer ? setSelectedTransferTemplateId : setSelectedTemplateId;

        if (!templateId || templateId === 'none') {
            setSelected('none');
            onChange({ target: { name: templateField, value: null } });
            onChange({ target: { name: variaveisField, value: {} } });
            return;
        }
        const templateIdStr = String(templateId);
        const template = templates.find((tpl) => String(tpl.templateId || tpl._id) === templateIdStr);
        if (!template) return;

        const comps = normalizarComponents(template?.components || []);
        const conteudo = comps?.find((c) => c.type === 'BODY')?.text || '';
        const variaveis = extrairVariaveisTemplate(comps);
        const templateData = {
            templateId: String(template.templateId || template._id),
            name: template.name || template.templateName,
            conteudo,
            components: comps,
        };

        setSelected(templateIdStr);
        onChange({ target: { name: templateField, value: templateData } });
        onChange({ target: { name: variaveisField, value: variaveis } });
    };

    useEffect(() => {
        const templateId = config?.generalConfig?.feedbackTemplate?.templateId
            ? String(config.generalConfig.feedbackTemplate.templateId)
            : 'none';
        if (selectedTemplateId !== templateId) {
            setSelectedTemplateId(templateId);
        }
    }, [config?.generalConfig?.feedbackTemplate?.templateId]);

    useEffect(() => {
        const templateId = config?.generalConfig?.transferTemplate?.templateId
            ? String(config.generalConfig.transferTemplate.templateId)
            : 'none';
        if (selectedTransferTemplateId !== templateId) {
            setSelectedTransferTemplateId(templateId);
        }
    }, [config?.generalConfig?.transferTemplate?.templateId]);

    useEffect(() => {
        if (isWhatsAppBusiness && (config?.generalConfig?.feedback || config?.generalConfig?.msgTransfer)) {
            buscarTemplates();
        }
    }, [isWhatsAppBusiness, config?.generalConfig?.feedback, config?.generalConfig?.msgTransfer, canal?.cliente_id, user?.clienteId]);

    useEffect(() => {
        if ((templateObrigatorio || transferTemplateObrigatorio) && templates.length === 0) {
            buscarTemplates();
        }
    }, [templateObrigatorio, transferTemplateObrigatorio, canal?.cliente_id, user?.clienteId]);

    const buscarChatbots = async () => {
        const idCliente = user?.clienteId;
        if (!idCliente) return;
        try {
            setLoadingChatbots(true);
            const res = await listChatbots(idCliente);
            setChatbotsList(res.data?.data || []);
        } catch (error) {
            console.log('Erro ao buscar chatbots:', error);
        } finally {
            setLoadingChatbots(false);
        }
    };

    useEffect(() => {
        if (canal?.webhook_type === 'chatbot') {
            buscarChatbots();
        }
    }, [canal?.webhook_type, canal?.cliente_id, user?.clienteId]);

    const newOpcoesMomentos = [
        { label: t('channels.ai.momentFinishLabel', 'Ao finalizar chat'), value: 'finalizar' },
        { label: t('channels.ai.momentDuringLabel', 'Durante o Chat'), value: 'durante' },
    ];

    const newListSectors = listSectors?.map((item) => ({ value: item._id, label: item.nome_setor }));

    const optionsAllocate = [
        {
            label: t('channels.sectors.allocateRoundRobinLabel', 'Distribuição uniforme'),
            value: 'round_robin',
            description: t('channels.sectors.allocateRoundRobinDesc', 'Round robin...'),
        },
        {
            label: t('channels.sectors.allocateRandomLabel', 'Aleatório'),
            value: 'random',
            description: t('channels.sectors.allocateRandomDesc', 'Aleatório...'),
        },
        {
            label: t('channels.sectors.allocateSequentialLabel', 'Distribuição sequencial'),
            value: 'sequential',
            description: t('channels.sectors.allocateSequentialDesc', 'Sequencial...'),
        },
    ];

    const optionsPeriod = [
        {
            label: t('channels.sectors.periodMonthLabel', 'Mensal (recomendado)'),
            value: 'month',
            description: t('channels.sectors.periodMonthDesc', 'Mensal...'),
        },
        {
            label: t('channels.sectors.periodDailyLabel', 'Diário'),
            value: 'daily',
            description: t('channels.sectors.periodDailyDesc', 'Diário...'),
        },
    ];

    // Prompts da análise de atendimento. São seis campos com a mesma forma —
    // a tabela evita repetir o mesmo bloco de textarea seis vezes.
    const promptsAnalise = [
        {
            field: 'prompt_alerts',
            name: 'ai.modules.aiAnalysisChat.prompt_alerts',
            label: t('channels.ai.promptAlertsLabel', 'Prompt para Alertas'),
            placeholder: t('channels.ai.promptAlertsPlaceholder', 'Digite o prompt para análise de alertas...'),
            help: t('channels.ai.promptAlertsHint', 'Instruções específicas para análise de alertas'),
        },
        {
            field: 'prompt_predictive',
            name: 'ai.modules.aiAnalysisChat.prompt_predictive',
            label: t('channels.ai.promptPredictiveLabel', 'Prompt Preditivo'),
            placeholder: t('channels.ai.promptPredictivePlaceholder', 'Digite o prompt para análise preditiva...'),
            help: t('channels.ai.promptPredictiveHint', 'Instruções para análise preditiva do atendimento'),
        },
        {
            field: 'prompt_resume',
            name: 'ai.modules.aiAnalysisChat.prompt_resume',
            label: t('channels.ai.promptResumeLabel', 'Prompt para Resumo'),
            placeholder: t('channels.ai.promptResumePlaceholder', 'Digite o prompt para resumo do atendimento...'),
            help: t('channels.ai.promptResumeHint', 'Instruções para geração do resumo do atendimento'),
        },
        {
            field: 'prompt_sentiment',
            name: 'ai.modules.aiAnalysisChat.prompt_sentiment',
            label: t('channels.ai.promptSentimentLabel', 'Prompt para Sentimento'),
            placeholder: t('channels.ai.promptSentimentPlaceholder', 'Digite o prompt para análise de sentimento...'),
            help: t('channels.ai.promptSentimentHint', 'Instruções para análise do sentimento do cliente'),
        },
        {
            field: 'prompt_subject',
            name: 'ai.modules.aiAnalysisChat.prompt_subject',
            label: t('channels.ai.promptSubjectLabel', 'Prompt para Assuntos'),
            placeholder: t('channels.ai.promptSubjectPlaceholder', 'Digite o prompt para identificação de assuntos...'),
            help: t('channels.ai.promptSubjectHint', 'Instruções para identificação dos assuntos tratados'),
        },
        {
            field: 'prompt_suggestion',
            name: 'ai.modules.aiAnalysisChat.prompt_suggestion',
            label: t('channels.ai.promptSuggestionLabel', 'Prompt para Sugestões'),
            placeholder: t('channels.ai.promptSuggestionPlaceholder', 'Digite o prompt para geração de sugestões...'),
            help: t('channels.ai.promptSuggestionHint', 'Instruções para geração de sugestões de melhoria'),
        },
    ];

    // Mensagens automáticas do canal. O ponto no trilho mostra se aquela mensagem
    // está de fato ativa: as que têm interruptor seguem o interruptor; as que não
    // têm (boas-vindas, direcionamento) valem quando têm texto.
    const mensagensAutomaticas = [
        {
            key: 'finish',
            label: t('channels.messages.tabFinish', 'Finalização'),
            title: t('channels.messages.finishTitle', 'Finalização de chat'),
            help: t('channels.messages.finishTooltip', 'Mensagem enviada ao cliente após a finalização de um chat.'),
            toggleName: 'generalConfig.msgFinish',
            toggleValue: config?.generalConfig?.msgFinish,
            toggleLabel: t('channels.messages.finishSwitchLabel', 'Enviar mensagem de finalização de chat'),
            textName: 'generalConfig.msgs.finish',
            textValue: config?.generalConfig?.msgs?.finish,
            variables: [],
        },
        {
            key: 'transfer',
            label: t('channels.messages.transferTitle', 'Transferência de chat'),
            title: t('channels.messages.transferTitle', 'Transferência de chat'),
            help: t('channels.messages.transferTooltip', 'Mensagem enviada ao cliente após ser transferido para um setor ou atendente.'),
            toggleName: 'generalConfig.msgTransfer',
            toggleValue: config?.generalConfig?.msgTransfer,
            toggleLabel: t('channels.messages.transferSwitchLabel', 'Enviar mensagem de transferência de chat'),
            textName: 'generalConfig.msgs.transfer',
            textValue: config?.generalConfig?.msgs?.transfer,
            // Únicas variáveis substituídas pelo servidor (v2_chats.controller):
            // {{nome}} = destino da transferência, {{nome_atendente}} = quem transferiu.
            variables: ['{{nome}}', '{{nome_atendente}}'],
        },
        {
            key: 'feedback',
            label: t('channels.messages.tabFeedback', 'Pesquisa de satisfação'),
            title: t('channels.messages.feedbackTitle', 'Pesquisa de satisfação'),
            help: t('channels.messages.feedbackHelp', 'Enviada após a finalização, para coletar a nota do atendimento.'),
            toggleName: 'generalConfig.feedback',
            toggleValue: config?.generalConfig?.feedback,
            toggleLabel: t('channels.messages.feedbackSwitchLabel', 'Enviar pesquisa de satisfação ao cliente'),
            textName: 'generalConfig.msgs.feedback',
            textValue: config?.generalConfig?.msgs?.feedback,
            variables: [],
        },
        {
            key: 'welcome',
            label: t('channels.messages.tabWelcome', 'Boas-vindas'),
            title: t('channels.messages.welcomeTitle', 'Mensagem de boas-vindas'),
            help: t('channels.messages.welcomeHelp', 'Primeira mensagem que o cliente recebe ao iniciar a conversa.'),
            toggleName: null,
            textName: 'generalConfig.msgs.welcome',
            textValue: config?.generalConfig?.msgs?.welcome,
            variables: [],
        },
        {
            key: 'afterTarget',
            label: t('channels.messages.tabAfterTarget', 'Direcionamento'),
            title: t('channels.messages.afterTargetTitle', 'Mensagem após direcionamento'),
            help: t('channels.messages.afterTargetHelp', 'Enviada assim que a conversa é direcionada a um atendente ou setor.'),
            toggleName: null,
            textName: 'generalConfig.msgs.afterTarget',
            textValue: config?.generalConfig?.msgs?.afterTarget,
            variables: [],
        },
    ].map((m) => ({
        ...m,
        ativa: m.toggleName ? Boolean(m.toggleValue) : Boolean(String(m.textValue ?? '').trim()),
    }));

    // Seletor da estratégia. Vive numa variável porque em "Direcionamento direto"
    // ele ocupa a primeira coluna do formulário do ChannelDirect — no design a
    // estratégia e o status inicial da conversa são lidos juntos.
    const estrategiaAtual = choices.find((item) => item.value === canal?.webhook_type);
    const campoEstrategia = (
        <div className={D.field}>
            <label className={D.label}>{t('channels.strategies.configLabel', 'Distribuição')}</label>
            <Select
                value={canal?.webhook_type || ''}
                onValueChange={(value) => onChangeChannel({ target: 'webhookType', value })}
            >
                <SelectTrigger className={D.select}>
                    <SelectValue placeholder={t('channels.strategies.selectPlaceholder', 'Selecione uma configuração')}>
                        {estrategiaAtual && (
                            <span className="flex items-center gap-2.5">
                                <Iconify icon={estrategiaAtual.icon} width={18} className="text-[#71717a]" />
                                {estrategiaAtual.name}
                            </span>
                        )}
                    </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                    {choices.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                            <div className="flex items-center gap-3">
                                <Iconify icon={item.icon} width={20} className="text-[#71717a]" />
                                <div className="flex flex-col">
                                    <span className="font-semibold">{item.name}</span>
                                    {item.description && <span className="text-xs text-[#71717a]">{item.description}</span>}
                                </div>
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {estrategiaAtual?.description && <p className={D.hint}>{estrategiaAtual.description}</p>}
        </div>
    );

    const usaDirecionamentoDireto = canal?.webhook_type === 'direct' && isEdit;

    // Blocos específicos da estratégia escolhida — o "direct" é montado dentro do
    // próprio cartão (via ChannelDirect) e por isso fica de fora daqui.
    const blocosEstrategia = (
        <>
            {(canal?.webhook_type === 'laisai_v2' || canal?.webhook_type === 'laisai_v3' || canal?.webhook_type === 'lais_agents') &&
                isEdit && (
                    <ChannelLaisAiV2
                        onChange={onChange}
                        canal={canal}
                        config={config}
                        listUsers={listUsers}
                        listSectors={listSectors}
                        user={user}
                        cliente={cliente}
                        changeAgent={changeAgent}
                    />
                )}
            {canal?.webhook_type === 'dialogflow_basic' && isEdit && (
                <ChannelDialogflow canal={canal} onChange={onChange} listUsers={listUsers} listSectors={listSectors} />
            )}
            {canal?.webhook_type === 'chatbot' && isEdit && (
                <div className={D.section}>
                    <h4 className={D.sectionTitle}>{t('channels.chatbot.sectionTitle', 'Selecione o Chatbot')}</h4>
                    <div className="mt-3.5 max-w-[420px]">
                        <Select
                            value={config?.chatflow?.chatflow_id || 'none'}
                            onValueChange={(value) => {
                                onChange({ target: { name: 'chatflow.chatflow_id', value: value === 'none' ? '' : value } });
                            }}
                            disabled={loadingChatbots}
                        >
                            <SelectTrigger className={D.select}>
                                <SelectValue
                                    placeholder={
                                        loadingChatbots
                                            ? t('channels.chatbot.loadingPlaceholder', 'Carregando chatbots...')
                                            : t('channels.chatbot.selectPlaceholder', 'Selecione um chatbot')
                                    }
                                />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                                <SelectItem value="none">
                                    <span className="text-[#a1a1aa]">{t('channels.chatbot.selectPlaceholder', 'Selecione um chatbot')}</span>
                                </SelectItem>
                                {chatbotsList.length === 0 ? (
                                    <div className={D.emptyState}>
                                        <Iconify icon="mdi:robot-off" width={32} className="mx-auto mb-2 text-[#d4d4d8]" />
                                        <p>{t('channels.chatbot.emptyState', 'Nenhum chatbot disponível')}</p>
                                    </div>
                                ) : (
                                    chatbotsList.map((chatbot) => (
                                        <SelectItem key={chatbot._id} value={chatbot._id}>
                                            <div className="flex items-center gap-3">
                                                <span className="w-8 h-8 rounded-lg bg-[#ede9fe] flex items-center justify-center">
                                                    <Iconify icon="mdi:robot" width={18} className="text-[#6d28d9]" />
                                                </span>
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-sm">{chatbot.name}</span>
                                                    {chatbot.description && (
                                                        <span className="text-xs text-[#71717a] truncate max-w-[200px]">
                                                            {chatbot.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))
                                )}
                            </SelectContent>
                        </Select>
                        {!config?.chatflow?.chatflow_id && (
                            <p className={D.hintWarn}>{t('channels.chatbot.requiredHint', 'Selecione um chatbot para continuar')}</p>
                        )}
                    </div>
                </div>
            )}
            {(canal?.webhook_type === 'laisai' ||
                canal?.webhook_type === 'laisai_v2' ||
                canal?.webhook_type === 'laisai_v3' ||
                canal?.webhook_type === 'lais_agents' ||
                canal?.webhook_type === 'chatbot' ||
                canal?.webhook_type === 'flowbuilder') &&
                isEdit && (
                    <AiSchedulePauseSection config={config} onChange={onChange} listUsers={listUsers} listSectors={listSectors} />
                )}
        </>
    );

    return (
        <>
            <Tabs defaultValue="config" className="w-full">
                {/* Cabeçalho e abas dividem o mesmo bloco fixo: no design a barra de
                    abas é a borda inferior do cabeçalho, não um elemento solto. */}
                <div className={D.stickyHeader}>
                    <div className={D.container}>
                        {header}
                        <TabsList className={D.tabsList}>
                            <TabsTrigger value="config" className={D.tab}>{t('channels.tabs.config', 'Configuração')}</TabsTrigger>
                            <TabsTrigger value="mensagens" className={D.tab}>{t('channels.tabs.messages', 'Mensagens')}</TabsTrigger>
                            <TabsTrigger value="setores" className={D.tab}>{t('channels.tabs.sectors', 'Atendimentos')}</TabsTrigger>
                            <TabsTrigger value="ia" className={D.tab}>{t('channels.tabs.ai', 'IA')}</TabsTrigger>
                            {canal?.platform === 'whatsapp' && (
                                <TabsTrigger value="saude" className={D.tab}>{t('channels.tabs.health', 'Saúde')}</TabsTrigger>
                            )}
                            {(isAdmin || verifyRules(user.rules, 'templates_whatsapp')) && canal?.platform === 'whatsapp_business' && (
                                <TabsTrigger value="templates" className={D.tab}>{t('channels.tabs.templates', 'Templates WhatsApp')}</TabsTrigger>
                            )}
                            {(isAdmin || verifyRules(user.rules, 'templates_messenger')) && canal?.platform === 'messenger' && (
                                <TabsTrigger value="templates-messenger" className={D.tab}>
                                    {t('channels.tabs.templatesMessenger', 'Templates Messenger')}
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </div>
                </div>

                <div className={`${D.container} ${D.tabsBody} ${D.pageBody}`}>
                    {/* ---------------------------------------------- Configuração */}
                    <TabsContent value="config" className="mt-0">
                        <div className={D.stack}>
                            {connectionBlocks}

                            <SectionHeader title={t('channels.sections.identity', 'Identificação')} />
                            <div className={D.card}>
                                <div className={D.grid2}>
                                    <div className={D.field}>
                                        <label className={D.label} htmlFor="channel-name">
                                            {t('channels.form.nameLabel', 'Nome do canal')}
                                        </label>
                                        <Input
                                            id="channel-name"
                                            name="channelName"
                                            value={canal?.nome || ''}
                                            onChange={onChange}
                                            className={D.input}
                                        />
                                        <p className={D.hint}>
                                            {t('channels.form.nameHintInternal', 'Nome interno, visível só para a sua equipe.')}
                                        </p>
                                    </div>
                                    <div className={D.field}>
                                        <span className={D.label}>{t('channels.form.channelLabel', 'Plataforma')}</span>
                                        <div className={D.readonlyField}>
                                            {plataformaAtual && <Iconify icon={plataformaAtual.icon} width={20} />}
                                            <span className={D.readonlyValue}>
                                                {plataformaAtual?.name || canal?.platform || '—'}
                                            </span>
                                            <span className={D.readonlyNote}>
                                                {t('channels.form.platformSetOnConnection', 'definido na conexão')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {identityLinkBlock}
                            </div>

                            {/* A conexão pelo Embedded Signup é feita em Contas Sociais e
                                trazida ao canal pelo vínculo; os campos abaixo continuam
                                valendo para quem configura na mão ou já estava conectado. */}
                            {canal?.platform === 'whatsapp_business' && (
                                <>
                                    <SectionHeader
                                        title={t('channels.sections.whatsappConnection', 'Conexão do WhatsApp Business')}
                                        help={t(
                                            'channels.sections.whatsappConnectionHelp',
                                            'Conecte a conta em Contas Sociais e vincule-a a este canal, ou preencha as credenciais manualmente.'
                                        )}
                                    />
                                    <div className={D.card}>
                                        <div className={`${D.noticeInfo} flex flex-col sm:flex-row sm:items-center gap-4`}>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[14px] font-semibold text-[#18181b]">
                                                    {t('channels.form.linkAccountTitle', 'Conectar pela Meta')}
                                                </p>
                                                <p className="mt-0.5">
                                                    {t(
                                                        'channels.form.linkAccountDescription',
                                                        'Conecte a conta em Contas Sociais e depois vincule-a a este canal na aba Detalhes.'
                                                    )}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => navigate('/dashboard/configuracoes/contas-sociais')}
                                                className={D.ghostButton}
                                            >
                                                <Iconify icon="eva:external-link-outline" width={16} />
                                                {t('channels.form.goToSocialAccountsButton', 'Contas Sociais')}
                                            </button>
                                        </div>

                                        <div className={`${D.grid2} mt-6`}>
                                            <div className={D.field}>
                                                <label className={D.label} htmlFor="wbo-phone-id">
                                                    {t('channels.form.phoneNumberIdLabel', 'Id número')}
                                                </label>
                                                <Input
                                                    id="wbo-phone-id"
                                                    className={D.input}
                                                    value={canal?.config?.meta?.whatsappBusinness?.fromNumberId || ''}
                                                    onChange={(e) =>
                                                        onChange({ target: { name: 'whatsapp_business_id', value: e.target.value } })
                                                    }
                                                />
                                            </div>
                                            <div className={D.field}>
                                                <label className={D.label} htmlFor="wbo-waba-id">
                                                    {t('channels.form.wabaIdLabel', 'Waba ID')}
                                                </label>
                                                <Input
                                                    id="wbo-waba-id"
                                                    className={D.input}
                                                    value={canal?.config?.meta?.whatsappBusinness?.wabaId || ''}
                                                    onChange={(e) => onChange({ target: { name: 'wabaId', value: e.target.value } })}
                                                />
                                            </div>
                                            {user?.fullAc && (
                                                <div className={`${D.field} md:col-span-2`}>
                                                    <label className={D.label} htmlFor="wbo-token">
                                                        {t('channels.form.tokenLabel', 'Token do WhatsApp Business Oficial')}
                                                    </label>
                                                    <Input
                                                        id="wbo-token"
                                                        className={`${D.input} ${D.inputMono}`}
                                                        value={canal?.config?.meta?.whatsappBusinness?.accessToken || ''}
                                                        onChange={(e) =>
                                                            onChange({
                                                                target: { name: 'whatsapp_business_token', value: e.target.value },
                                                            })
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            <SectionHeader
                                title={t('channels.strategies.sectionTitle', 'Estratégia de atendimento')}
                                help={t(
                                    'channels.strategies.sectionSubtitleFlow',
                                    'Como as conversas que chegam por este canal são distribuídas.'
                                )}
                            />
                            <div className={D.card}>
                                {usaDirecionamentoDireto ? (
                                    <ChannelDirect
                                        leadingField={campoEstrategia}
                                        listUsers={listUsers}
                                        listSectors={listSectors}
                                        canal={canal}
                                        onChange={onChange}
                                        onChangeAutocomplete={onChangeAutocomplete}
                                    />
                                ) : (
                                    <>
                                        <div className={D.grid2}>{campoEstrategia}</div>
                                        {blocosEstrategia}
                                    </>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* -------------------------------------------------- Mensagens */}
                    <TabsContent value="mensagens" className="mt-0">
                        {isEdit && (
                            <div className={D.stack}>
                                <SectionHeader
                                    title={t('channels.sections.autoMessages', 'Mensagens automáticas')}
                                    help={t(
                                        'channels.sections.autoMessagesHelp',
                                        'Textos disparados pelo sistema em cada momento do atendimento.'
                                    )}
                                />
                                <Tabs defaultValue="finish" orientation="vertical" className={D.railWrap}>
                                    <TabsList className={D.railCol}>
                                        {mensagensAutomaticas.map((m) => (
                                            <TabsTrigger key={m.key} value={m.key} className={D.railItem}>
                                                <span className={`${D.railDot} ${m.ativa ? D.railDotOn : D.railDotOff}`} />
                                                {m.label}
                                            </TabsTrigger>
                                        ))}
                                    </TabsList>

                                    {mensagensAutomaticas.map((m) => (
                                        <TabsContent key={m.key} value={m.key} className={`${D.railPane} mt-0`}>
                                            <div className={D.railPaneHead}>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className={D.cardTitle}>{m.title}</h3>
                                                    <p className={D.cardSubtitle}>{m.help}</p>
                                                    {m.key === 'feedback' &&
                                                        templateObrigatorio &&
                                                        !config?.generalConfig?.feedbackTemplate && (
                                                            <p className={D.hintError}>
                                                                {t(
                                                                    'channels.messages.templateRequiredAlertBody',
                                                                    'Como o canal é WhatsApp Business, é obrigatório selecionar um template para enviar pesquisa de satisfação.'
                                                                )}
                                                            </p>
                                                        )}
                                                    {m.key === 'transfer' &&
                                                        transferTemplateObrigatorio &&
                                                        !config?.generalConfig?.transferTemplate && (
                                                            <p className={D.hintError}>
                                                                {t(
                                                                    'channels.messages.transferTemplateRequiredAlertBody',
                                                                    'Como o canal é WhatsApp Business, é obrigatório selecionar um template para enviar a mensagem de transferência fora da janela de 24 horas.'
                                                                )}
                                                            </p>
                                                        )}
                                                </div>
                                                {m.toggleName && (
                                                    <Switch
                                                        aria-label={m.toggleLabel}
                                                        checked={Boolean(m.toggleValue)}
                                                        className={D.switch}
                                                        onCheckedChange={(value) => {
                                                            onChange({ target: { name: m.toggleName, value } });
                                                            if (m.key === 'feedback' && isWhatsAppBusiness) {
                                                                if (value) {
                                                                    buscarTemplates();
                                                                } else {
                                                                    onChange({ target: { name: 'generalConfig.feedbackTemplate', value: null } });
                                                                    onChange({
                                                                        target: { name: 'generalConfig.feedbackTemplateVariaveis', value: {} },
                                                                    });
                                                                }
                                                            }
                                                            if (m.key === 'transfer' && isWhatsAppBusiness) {
                                                                if (value) {
                                                                    buscarTemplates();
                                                                } else {
                                                                    onChange({ target: { name: 'generalConfig.transferTemplate', value: null } });
                                                                    onChange({
                                                                        target: { name: 'generalConfig.transferTemplateVariaveis', value: {} },
                                                                    });
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </div>

                                            <MessageEditor
                                                label={t('channels.messages.textLabel', 'Texto da mensagem')}
                                                rows={7}
                                                value={m.textValue}
                                                variables={m.variables}
                                                onChange={(value) => onChange({ target: { name: m.textName, value } })}
                                            />

                                            {m.key === 'feedback' && (
                                                <WhatsAppTemplatePicker
                                                    t={t}
                                                    D={D}
                                                    enabled={isWhatsAppBusiness && config?.generalConfig?.feedback}
                                                    obrigatorio={templateObrigatorio}
                                                    template={config?.generalConfig?.feedbackTemplate}
                                                    templateVariaveis={config?.generalConfig?.feedbackTemplateVariaveis}
                                                    selectedTemplateId={selectedTemplateId}
                                                    templates={templates}
                                                    loadingTemplates={loadingTemplates}
                                                    normalizarComponents={normalizarComponents}
                                                    onSelectTemplate={handleSelecionarTemplate}
                                                    onFetchTemplates={buscarTemplates}
                                                    onChangeVariavel={(key, value) => {
                                                        const variaveisAtuais = config?.generalConfig?.feedbackTemplateVariaveis || {};
                                                        onChange({
                                                            target: {
                                                                name: 'generalConfig.feedbackTemplateVariaveis',
                                                                value: { ...variaveisAtuais, [key]: value },
                                                            },
                                                        });
                                                    }}
                                                    showMessageRequiredHint={
                                                        !config?.generalConfig?.msgs?.feedback
                                                    }
                                                    messageRequiredHint={t(
                                                        'channels.messages.feedbackMessageRequiredHint',
                                                        'É necessário preencher o campo de mensagem para enviar dentro da janela de 24 horas'
                                                    )}
                                                />
                                            )}

                                            {m.key === 'transfer' && (
                                                <WhatsAppTemplatePicker
                                                    t={t}
                                                    D={D}
                                                    enabled={isWhatsAppBusiness && config?.generalConfig?.msgTransfer}
                                                    obrigatorio={transferTemplateObrigatorio}
                                                    template={config?.generalConfig?.transferTemplate}
                                                    templateVariaveis={config?.generalConfig?.transferTemplateVariaveis}
                                                    selectedTemplateId={selectedTransferTemplateId}
                                                    templates={templates}
                                                    loadingTemplates={loadingTemplates}
                                                    normalizarComponents={normalizarComponents}
                                                    onSelectTemplate={handleSelecionarTransferTemplate}
                                                    onFetchTemplates={buscarTemplates}
                                                    onChangeVariavel={(key, value) => {
                                                        const variaveisAtuais = config?.generalConfig?.transferTemplateVariaveis || {};
                                                        onChange({
                                                            target: {
                                                                name: 'generalConfig.transferTemplateVariaveis',
                                                                value: { ...variaveisAtuais, [key]: value },
                                                            },
                                                        });
                                                    }}
                                                    showMessageRequiredHint={
                                                        !config?.generalConfig?.msgs?.transfer
                                                    }
                                                    messageRequiredHint={t(
                                                        'channels.messages.transferMessageRequiredHint',
                                                        'É necessário preencher o campo de mensagem para enviar dentro da janela de 24 horas'
                                                    )}
                                                />
                                            )}
                                        </TabsContent>
                                    ))}
                                </Tabs>
                            </div>
                        )}
                    </TabsContent>

                    {/* ----------------------------------------------- Atendimentos */}
                    <TabsContent value="setores" className="mt-0">
                        {isEdit && (
                            <div className={D.stack}>
                                <SectionHeader title={t('channels.sections.queue', 'Setor e fila')} />
                                <div className={D.card}>
                                    <div className={D.grid2}>
                                        <div className={D.field}>
                                            <label className={D.label} htmlFor="sla-aceite">
                                                {t('channels.sectors.slaLabelShort', 'SLA de aceite')}
                                            </label>
                                            <div className={D.inputSuffixRow}>
                                                <Input
                                                    id="sla-aceite"
                                                    type="number"
                                                    min={1}
                                                    placeholder="5"
                                                    className={`${D.input} ${D.inputShort}`}
                                                    value={config?.sla_tempo_aceite ? config.sla_tempo_aceite / 60 : ''}
                                                    onChange={(e) => onChange({ target: { name: 'sla_tempo_aceite', value: e.target.value } })}
                                                />
                                                <span className={D.inputSuffix}>{t('channels.sectors.slaUnit', 'minutos')}</span>
                                            </div>
                                            <p className={D.hint}>
                                                {t('channels.sectors.slaHint', 'Tempo máximo para um atendente aceitar a conversa.')}
                                            </p>
                                        </div>
                                        <div className={D.field}>
                                            <label className={D.label}>{t('channels.sectors.defaultSectorLabel', 'Setor padrão')}</label>
                                            <Select
                                                value={config?.departamentConfig?.defaultSector || ''}
                                                onValueChange={(value) =>
                                                    onChange({ target: { name: 'departamentConfig.defaultSector', value } })
                                                }
                                            >
                                                <SelectTrigger className={D.select}>
                                                    <SelectValue
                                                        placeholder={t('channels.sectors.defaultSectorPlaceholder', 'Selecione um setor padrão')}
                                                    >
                                                        {newListSectors?.find(
                                                            (item) => item.value === config?.departamentConfig?.defaultSector
                                                        )?.label || null}
                                                    </SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {newListSectors?.map((item) => (
                                                        <SelectItem key={item.value} value={item.value}>
                                                            {item.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className={D.hint}>
                                                {t('channels.sectors.defaultSectorHint', 'Usado quando nenhum setor é identificado.')}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <SectionHeader title={t('channels.sections.distribution', 'Distribuição')} />
                                <div className={D.cardRows}>
                                    <ToggleRow
                                        id="wallet-status"
                                        label={t('channels.sectors.walletLabelPlain', 'Carteirização de clientes')}
                                        help={t(
                                            'channels.sectors.walletTooltip',
                                            'O cliente volta sempre para o último atendente que falou com ele.'
                                        )}
                                        beta
                                        checked={config?.departamentConfig?.wallet?.status}
                                        onCheckedChange={(value) =>
                                            onChange({ target: { name: 'departamentConfig.wallet.status', value } })
                                        }
                                    />
                                    <ToggleRow
                                        id="allocate-status"
                                        label={t('channels.sectors.allocateLabel', 'Distribuir atendimentos entre usuários do setor')}
                                        help={t(
                                            'channels.sectors.allocateTooltip',
                                            'Divide automaticamente as conversas novas entre quem está no setor.'
                                        )}
                                        checked={config?.departamentConfig?.allocate?.status}
                                        onCheckedChange={(value) =>
                                            onChange({ target: { name: 'departamentConfig.allocate.status', value } })
                                        }
                                    />
                                    <ToggleRow
                                        id="allocate-ignore-off"
                                        label={t('channels.sectors.ignoreUsersOffLabel', 'Ignorar usuários em intervalo')}
                                        help={t('channels.sectors.ignoreUsersOffTooltip', 'Quem estiver em pausa não recebe novas conversas.')}
                                        checked={config?.departamentConfig?.allocate?.ignoreUsersOff}
                                        onCheckedChange={(value) =>
                                            onChange({ target: { name: 'departamentConfig.allocate.ignoreUsersOff', value } })
                                        }
                                    />

                                    <div className="py-5">
                                        <span className={D.label}>
                                            {t('channels.sectors.allocateTypeLabel', 'Como dividir entre os atendentes')}
                                        </span>
                                        <div className={`${D.choiceGrid} mt-1`}>
                                            {optionsAllocate.map((option) => (
                                                <ChoiceCard
                                                    key={option.value}
                                                    label={option.label}
                                                    help={option.description}
                                                    selected={config?.departamentConfig?.allocate?.type === option.value}
                                                    onSelect={() =>
                                                        onChange({ target: { name: 'departamentConfig.allocate.type', value: option.value } })
                                                    }
                                                />
                                            ))}
                                        </div>

                                        {config?.departamentConfig?.allocate?.type === 'sequential' && (
                                            <div className="mt-5">
                                                <span className={D.label}>
                                                    {t('channels.sectors.periodLabel', 'Período de distribuição')}
                                                </span>
                                                <div className={`${D.choiceGrid} mt-1`}>
                                                    {optionsPeriod.map((option) => (
                                                        <ChoiceCard
                                                            key={option.value}
                                                            label={option.label}
                                                            help={option.description}
                                                            selected={config?.departamentConfig?.allocate?.period === option.value}
                                                            onSelect={() =>
                                                                onChange({
                                                                    target: { name: 'departamentConfig.allocate.period', value: option.value },
                                                                })
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </TabsContent>

                    {/* --------------------------------------------------------- IA */}
                    <TabsContent value="ia" className="mt-0">
                        <div className={D.stack}>
                            <SectionHeader
                                title={t('channels.ai.laisGoTitle', 'Lais Go')}
                                help={t('channels.ai.laisGoSectionHelp', 'Comportamento da IA dentro das conversas.')}
                            />
                            <div className={D.cardRows}>
                                <ToggleRow
                                    id="lais-go-status"
                                    last
                                    label={t('channels.ai.laisGoSwitchLabel', 'Silenciar IA com etiqueta')}
                                    help={t(
                                        'channels.ai.laisGoTooltip',
                                        'A IA para de responder quando a conversa recebe uma etiqueta no WhatsApp (aparelho).'
                                    )}
                                    checked={config?.laisGo?.status}
                                    onCheckedChange={(value) => onChange({ target: { name: 'laisGo.status', value } })}
                                />
                            </div>

                            {cliente && cliente.features && verifyFeatures(cliente.features, 'ai.analise_atendimento') && (
                                <>
                                    <SectionHeader
                                        title={t('channels.ai.analysisTitle', 'Análise de atendimento')}
                                        help={t('channels.ai.analysisSectionHelp', 'Resumos e avaliações geradas automaticamente.')}
                                    />
                                    <div className={D.cardRows}>
                                        <ToggleRow
                                            id="ai-analysis-status"
                                            label={t('channels.ai.analysisSwitchLabel', 'Ativar análise de atendimento')}
                                            help={t('channels.ai.analysisStatusHint', 'Gera uma leitura automática do que aconteceu na conversa.')}
                                            checked={config?.ai?.modules?.aiAnalysisChat?.status}
                                            onCheckedChange={(value) =>
                                                onChange({ target: { name: 'ai.modules.aiAnalysisChat.status', value } })
                                            }
                                        />
                                        <ToggleRow
                                            id="ai-custom-prompts"
                                            last={!config?.ai?.modules?.aiAnalysisChat?.status}
                                            label={t('channels.ai.customPromptsLabel', 'Usar prompts customizados')}
                                            help={t('channels.ai.customPromptsHint', 'Escreva instruções próprias para cada tipo de análise.')}
                                            checked={config?.ai?.modules?.aiAnalysisChat?.customPrompts}
                                            onCheckedChange={(value) =>
                                                onChange({ target: { name: 'ai.modules.aiAnalysisChat.customPrompts', value } })
                                            }
                                        />

                                        {config?.ai?.modules?.aiAnalysisChat?.status && (
                                            <div className={`${D.grid2} py-5`}>
                                                <div className={D.field}>
                                                    <label className={D.label} htmlFor="ai-number-msgs">
                                                        {t('channels.ai.analysisMessagesCountLabel', 'Mensagens até a próxima análise')}
                                                    </label>
                                                    <Input
                                                        id="ai-number-msgs"
                                                        type="number"
                                                        min={1}
                                                        className={`${D.input} ${D.inputShort}`}
                                                        value={config?.ai?.modules?.aiAnalysisChat?.numberMsgs ?? ''}
                                                        onChange={(event) =>
                                                            onChange({
                                                                target: {
                                                                    name: 'ai.modules.aiAnalysisChat.numberMsgs',
                                                                    value: Number(event.target.value),
                                                                },
                                                            })
                                                        }
                                                    />
                                                    <p className={D.hint}>
                                                        {t(
                                                            'channels.ai.analysisMessagesCountHint',
                                                            'Quantas mensagens do cliente disparam uma nova análise.'
                                                        )}
                                                    </p>
                                                </div>
                                                <div className={D.field}>
                                                    <label className={D.label}>
                                                        {t('channels.ai.analysisMomentLabelShort', 'Quando gerar a análise')}
                                                    </label>
                                                    <MultiSelect
                                                        options={newOpcoesMomentos}
                                                        defaultValue={config?.ai?.modules?.aiAnalysisChat?.operation}
                                                        onValueChange={(newValue) =>
                                                            onChange({ target: { name: 'ai.modules.aiAnalysisChat.operation', value: newValue } })
                                                        }
                                                        hasSelectAll
                                                        className={D.multiSelect}
                                                        placeholder={t('channels.ai.analysisMomentPlaceholder', 'Selecione os momentos')}
                                                    />
                                                    <p className={D.hint}>
                                                        {t('channels.ai.analysisMomentHint', 'Selecione quando a análise deve ser gerada.')}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {config?.ai?.modules?.aiAnalysisChat?.customPrompts && (
                                            <div className="flex flex-col gap-5 py-5 border-t border-[#f0f0f3]">
                                                {promptsAnalise.map((prompt) => (
                                                    <div key={prompt.name} className={D.field}>
                                                        <label className={D.label} htmlFor={prompt.name}>
                                                            {prompt.label}
                                                        </label>
                                                        <Textarea
                                                            id={prompt.name}
                                                            rows={3}
                                                            className={D.textarea}
                                                            placeholder={prompt.placeholder}
                                                            value={config?.ai?.modules?.aiAnalysisChat?.[prompt.field] || ''}
                                                            onChange={(event) =>
                                                                onChange({ target: { name: prompt.name, value: event.target.value } })
                                                            }
                                                        />
                                                        <p className={D.hint}>{prompt.help}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            <SectionHeader
                                title={t('channels.ai.conversationTitle', 'Conversação por IA')}
                                help={t(
                                    'channels.ai.conversationSectionHelp',
                                    'Conversão de mídia em texto para a IA entender o atendimento.'
                                )}
                            />
                            <div className={D.cardRows}>
                                <ToggleRow
                                    id="ai-transcribe-audio"
                                    label={t('channels.ai.transcribeAudioLabel', 'Transcrição de áudio')}
                                    help={t(
                                        'channels.ai.transcribeAudioHint',
                                        'Transcreve os áudios para texto automaticamente. Obrigatório para a análise de atendimento.'
                                    )}
                                    checked={config?.ai?.autoTranscribeAudio}
                                    onCheckedChange={(value) => onChange({ target: { name: 'ai.autoTranscribeAudio', value } })}
                                />
                                <ToggleRow
                                    id="ai-transcribe-image"
                                    last
                                    label={t('channels.ai.transcribeImageLabel', 'Transcrição de imagens')}
                                    help={t('channels.ai.transcribeImageHint', 'Descreve automaticamente as imagens enviadas pelo cliente.')}
                                    checked={config?.ai?.autoTranscribeImage}
                                    onCheckedChange={(value) => onChange({ target: { name: 'ai.autoTranscribeImage', value } })}
                                />
                            </div>
                        </div>
                    </TabsContent>

                    {/* ------------------------------------------- abas específicas */}
                    {canal?.platform === 'whatsapp' && (
                        <TabsContent value="saude" className="mt-0">
                            <div className={D.card}>
                                <ChannelHealth canal={canal} session={canal?.config?.sessao} />
                            </div>
                        </TabsContent>
                    )}
                    <TabsContent value="templates" className="mt-0">
                        <div className={D.card}>
                            <TemplatesWhatsappPage />
                        </div>
                    </TabsContent>
                    <TabsContent value="templates-messenger" className="mt-0">
                        <div className={D.card}>
                            <TemplatesMessengerPage />
                        </div>
                    </TabsContent>
                    {/* Identificação e Horário do webchat foram movidos para Configurações → Central do Widget. */}
                </div>
            </Tabs>

            {!isEdit && (
                <div className={`${D.container} ${D.pageBody}`}>
                    <button type="button" onClick={onSubmit} className={D.primaryButton}>
                        {t('channels.actions.createChannel', 'Criar canal')}
                    </button>
                </div>
            )}
        </>
    );
}

